import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Layout from "../../../components/layout/Layout";
import FilterableTabCard from "../../../components/tabs/FilterableTabCard";
import { PublicationsHistory, PublishedReports, VotesSpread } from "./tabs";
import FilterPanel from "../../../components/filters/FilterPanel";
import CustomDrawer from "../../../components/layout/CustomDrawer";
import CustomToggle from "../../../components/common/CustomToggle";
import CommonNavigator from "../../../components/common/CommonNavigator";
import {
    useGetAnalystListQuery,
    useGetDynamicFilterOptionsQuery,
} from "../../../redux/service/modules/dashboard";
import { handleExport } from "../../../redux/service/modules/exportToExcel";
import {
    useLazyGetLazyPublicationCalendarHistoryQuery,
    useLazyGetLazyPublicationsExtractQuery,
    useLazyGetLazyAnalystsVotesSpreadQuery,
  } from "../../../redux/service/modules/dashboard/lazzyQuery";
import { convertToDates } from "../../../utils/commonUtils";

const WeightedScoreToggle = React.memo(({ onChange, selectedValue }) => {
    const options = ["Vote", "Weighted"];
    return (
        <CustomToggle
            options={options}
            onChange={onChange}
            selectedValue={selectedValue || options[0]}
        />
    );
});

const createTabs = (filterDetails, setFilterDetails) => [
    {
        id: "publications-history",
        label: "Publications History",
        content: PublicationsHistory,
        showFilter: true,
    },
    {
        id: "published-reports",
        label: "Published Reports",
        content: PublishedReports,
        showFilter: true,
    },
    {
        id: "votes-spread",
        label: "Votes Spread",
        content: VotesSpread,
        showFilter: true,
        customElmInTabHeader: () => (
            <WeightedScoreToggle
                onChange={(val) =>
                    setFilterDetails((prev) => ({
                        ...prev,
                        appliedFilters: {
                            ...prev.appliedFilters,
                            is_weighted: val,
                        },
                    }))
                }
                selectedValue={filterDetails?.appliedFilters?.is_weighted}
            />
        ),
    },
];

const HistoricalReports = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isFilterOpen, toggleFilter] = useState(false);
    const dateRange = useSelector((state) => state.date);
    const [requiredFilters, setRequiredFilters] = useState([]);
    const [filters, setFilters] = useState([]);
    const [filterDetails, setFilterDetails] = useState({
        appliedFilters: {
            is_weighted: "Vote",
            initialDateRange: dateRange,
            analyst: [],
            sector: [],
            report_type: [],
            report_type_key: [],
        },
        filterCount: 0,
        rawFilters: {},
    });

    const tabs = createTabs(filterDetails, setFilterDetails);
    const currentTabId = searchParams.get("tab") || "publications-history";
    const [activeTab, setActiveTab] = useState(
        tabs.findIndex((tab) => tab.id === currentTabId)
    );

    const { data: analystList } = useGetAnalystListQuery();
    const { data: dynamicFilterOptions } = useGetDynamicFilterOptionsQuery({
        filters: requiredFilters,
    });

    const [publicationHistoryExcelQuery] = useLazyGetLazyPublicationCalendarHistoryQuery()
    const [publicationExtractExcelQuery] = useLazyGetLazyPublicationsExtractQuery()
    const [voteExcelQuery] = useLazyGetLazyAnalystsVotesSpreadQuery()

    const exportModule = {
        "publications-history": publicationHistoryExcelQuery,
        "published-reports": publicationExtractExcelQuery,
        "votes-spread": voteExcelQuery
    }

    useEffect(() => {
        const availableFiltersMap = {
            analyst: {
                name: "analystName",
                label: "Analyst Name",
                key: "analyst",
                options:
                    analystList?.map((analyst) => ({
                        label: analyst.username,
                        id: analyst.id,
                    })) || [],
                multiSelect: true,
            },
            sector: {
                name: "sectorName",
                label: "Sector",
                key: "sector",
                options:
                    dynamicFilterOptions?.sector?.map((sector) => ({
                        label: sector.label,
                        id: sector.label,
                    })) || [],
                multiSelect: true,
            },
            report_type: {
                name: "reportType",
                label: "Report Type",
                key: "report_type",
                options:
                    dynamicFilterOptions?.report_type?.map((type) => ({
                        label: type,
                        id: type,
                    })) || [],
                multiSelect: true,
            },
            report_type_key: {
                name: "reportTypeKey",
                label: "Report Type",
                key: "report_type_key",
                options:
                    dynamicFilterOptions?.report_type_key?.map((type) => ({
                        label: type.label,
                        id: type.value,
                    })) || [],
                multiSelect: true,
            },
        };

        // Update filters based on required filters and new data
        const activeFilters = requiredFilters
            .map((filterKey) => availableFiltersMap[filterKey])
            .filter(Boolean);

        setFilters(activeFilters);
    }, [requiredFilters, analystList, dynamicFilterOptions]);

    const onFilterApply = (transformedFilters, filterCount, rawFilters) => {
        toggleFilter(false);
        setFilterDetails({
            appliedFilters: {
                ...transformedFilters,
                is_weighted: filterDetails.appliedFilters.is_weighted,
                initialDateRange: dateRange,
                analyst: transformedFilters.analyst || [],
                sector: transformedFilters.sector || [],
                report_type: transformedFilters.report_type || [],
                report_type_key: transformedFilters.report_type_key || [],
            },
            filterCount,
            rawFilters,
        });
    };

    const handleTabChange = (newValue) => {
        setActiveTab(newValue);
        setSearchParams({ tab: tabs[newValue].id });
        setFilterDetails({
            appliedFilters: {
                is_weighted: "Vote",
                initialDateRange: dateRange,
                analyst: [],
                sector: [],
                report_type: [],
                report_type_key: [],
            },
            filterCount: 0,
            rawFilters: {},
        });
    };
    const triggerExportToExcel = () => {
        handleExport(exportModule[tabs[activeTab].id], {appliedFilters: filterDetails.appliedFilters, ...(dateRange) || {}})
    }

    return (
        <Layout>
            <div>
                <CommonNavigator title={"Historical Reports"} showExportToExcel={true} triggerExportToExcel={triggerExportToExcel}/>
                <div className="mt-2">
                    <FilterableTabCard
                        tabs={tabs}
                        onTabClick={handleTabChange}
                        onFilterClick={() => toggleFilter(true)}
                        appliedFilters={filterDetails.appliedFilters}
                        filterCount={filterDetails.filterCount}
                        setRequiredFilters={setRequiredFilters}
                        value={activeTab}
                    />
                    <CustomDrawer open={isFilterOpen}>
                        <FilterPanel
                            key={JSON.stringify(filters)}
                            filtersContent={filters}
                            handlePanelClose={() => toggleFilter(false)}
                            onApply={onFilterApply}
                            appliedFilters={filterDetails.rawFilters}
                        />
                    </CustomDrawer>
                </div>
            </div>
        </Layout>
    );
};

export default HistoricalReports;
